/**
 * Carga la playlist del servidor — la "51 ESCUADRÓN PLAYLIST" (default).
 *
 * Al arrancar la radio se hace `fetch` de `/music/index.json` (el manifiesto
 * generado por `scripts/build-music-manifest.mjs`) y cada entrada se
 * convierte en una `Track` de servidor (`isUser: false`, `file` = URL
 * estática real, no una blob URL). Esas tracks rellenan la default
 * playlist, que es la misma para todos los pilotos.
 *
 * Diseño:
 *  - La default playlist es server-backed: su contenido lo fija SIEMPRE el
 *    manifiesto, no localStorage (ver `loadPlaylists` en persistence.ts).
 *  - Si el manifiesto falla (404, red caída, JSON corrupto) la radio
 *    degrada sin romper: la default queda vacía y el resto funciona igual.
 */

import { isPlainObject } from '@51esc/shared';
import { DEFAULT_PLAYLIST } from './data.js';
import { hashName } from './helpers.js';
import { getCallbacks } from './controls.js';
import { renderAll } from './render.js';
import { getPlaylist, setServerTracks, state } from './state.js';
import type { Track } from './types.js';

/** URL del manifiesto. Servido como estático desde `web/public/music/`. */
const MANIFEST_URL = '/music/index.json';

/** Forma mínima de una entrada del manifiesto. */
interface ManifestTrack {
  name: string;
  file: string;
}

/** ID estable de una track de servidor a partir de su nombre. */
function serverTrackId(name: string): string {
  return 's_' + hashName(name);
}

/** Valida defensivamente una entrada cualquiera del manifiesto. */
function isManifestTrack(v: unknown): v is ManifestTrack {
  return isPlainObject(v) && typeof v.name === 'string' && typeof v.file === 'string';
}

/**
 * Descarga el manifiesto, construye las server tracks, rellena la default
 * playlist y re-renderiza. No lanza nunca: cualquier fallo degrada a "sin
 * música de servidor" con un warning en consola.
 */
export async function loadServerMusic(): Promise<void> {
  let tracks: Track[] = [];
  try {
    const res = await fetch(MANIFEST_URL, { cache: 'no-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const json: unknown = await res.json();
    const list = isPlainObject(json) ? json.tracks : null;
    if (!Array.isArray(list)) throw new Error('manifiesto sin array `tracks`');
    tracks = list.filter(isManifestTrack).map((m) => ({
      id: serverTrackId(m.name),
      name: m.name,
      file: m.file,
      isUser: false,
    }));
  } catch (e) {
    console.warn('[radio] no se pudo cargar la música del servidor:', e);
    return;
  }

  setServerTracks(tracks);

  // La default playlist refleja siempre el manifiesto actual del servidor.
  const def = getPlaylist(state, DEFAULT_PLAYLIST.id);
  if (def) def.tracks = tracks.map((t) => t.id);

  renderAll(getCallbacks());
}
